import React, {useEffect, useState} from "react";
import {AgeInput, Button, Input, RedAsterisk} from "../../../components";
import RadioGroupField from "../../components/FormContent/RadioGroupField/RadioGroupField";
import {MemberGroup} from "src/ClientManagement/models/InvestorGroupType";
import {createFamilyMemberDropdownItems, parseFamilyMembers} from "../../utils/DefUtils";
import {AdditionalDistributionLayer, FamilyMembers, TriggerEventType} from "../../model/EstateFlowchartTypes";
import DropdownField from "../../components/FormContent/DropdownField/DropdownField";
import {DISTRIBUTION_CONSTANTS as Labels} from "../../constants/LabelConstants";
import {useAppSelector} from "../../../store/hooks";
import {saveSelector} from "../../slices/NavigationSlice";
import {EVENT_NAME_MAX_LENGTH} from "../../validation/ValidationUtils";

interface AdditionalDistributionLayerEventTriggerProps {
    layer: AdditionalDistributionLayer;
    familyMembers: MemberGroup;
    onChange: (layer: AdditionalDistributionLayer) => void;
    onReset: () => void;
}

const TRIGGER_EVENT_OPTIONS: { label: string; value: TriggerEventType }[] = [
    {label: "Death", value: "DEATH"},
    {label: "Age Attainment", value: "AGE_ATTAINMENT"},
    {label: "Other", value: "OTHER"}
];

const getTriggerMemberValue = (layer: AdditionalDistributionLayer): string => (
    layer.triggerMemberId ?? layer.triggerMemberName ?? ""
);

const getTriggerMemberSelection = (layer: AdditionalDistributionLayer): string | undefined => (
    layer.triggerMemberName ?? layer.triggerMemberId
);

const AdditionalDistributionLayerEventTrigger: React.FC<AdditionalDistributionLayerEventTriggerProps> = ({
    layer,
    familyMembers,
    onChange,
    onReset
}) => {
    const [pendingTriggerEventText, setPendingTriggerEventText] = useState(layer.triggerEventText ?? "");
    const [pendingTriggerAge, setPendingTriggerAge] = useState<number | null>(layer.triggerAge ?? null);
    const [parsedFamilyMembers, setParsedFamilyMembers] = useState<FamilyMembers>({value: "", label: "", familyMember: []});
    const saveHasBeenClicked = useAppSelector(saveSelector);

    useEffect(() => {
        setPendingTriggerEventText(layer.triggerEventText ?? "");
        setPendingTriggerAge(layer.triggerAge ?? null);
    }, [layer.triggerEventText, layer.triggerAge, layer.triggerEventType]);

    useEffect(() => {
        const parsedFamilyMembersResponse: FamilyMembers = parseFamilyMembers(familyMembers);
        const primaryMemberItem = {
            label: parsedFamilyMembersResponse.label,
            value: parsedFamilyMembersResponse.value,
            selected: false
        };
        parsedFamilyMembersResponse.familyMember.push(primaryMemberItem);
        setParsedFamilyMembers(parsedFamilyMembersResponse);
    }, [familyMembers]);

    const handleEventTriggerChange = (label: string) => {
        const nextValue: TriggerEventType = TRIGGER_EVENT_OPTIONS.find((option) => option.label === label)?.value ?? "";
        const shouldResetMember = nextValue !== layer.triggerEventType;
        let nextTriggerMemberId = "";
        let nextTriggerMemberName: string | undefined = layer.triggerMemberName;
        if (nextValue === "DEATH" || nextValue === "AGE_ATTAINMENT") {
            nextTriggerMemberId = shouldResetMember ? "" : layer.triggerMemberId;
            nextTriggerMemberName = shouldResetMember ? undefined : layer.triggerMemberName;
        }
        const nextLayer: AdditionalDistributionLayer = {
            ...layer,
            triggerEventType: nextValue,
            triggerAge: nextValue === "AGE_ATTAINMENT" ? (layer.triggerAge ?? null) : null,
            triggerEventText: nextValue === "OTHER" ? layer.triggerEventText : "",
            triggerMemberId: nextTriggerMemberId,
            triggerMemberName: nextTriggerMemberName
        };
        onChange(nextLayer);
    };

    const handleSelectedMemberChange = (triggerMemberId: string) => {
        const selectedMember = parsedFamilyMembers.familyMember.find(
            (member) => member.value === triggerMemberId
        );
        const isKnownMember = !!selectedMember;
        const hasFamilyMembers = parsedFamilyMembers.familyMember.length > 0;

        if (!isKnownMember && !hasFamilyMembers) {
            return;
        }
        onChange({
            ...layer,
            triggerMemberId: isKnownMember ? triggerMemberId : "",
            triggerMemberName: isKnownMember ? undefined : triggerMemberId
        });
    };

    const handleEventAgeChange = (value: number) => {
        setPendingTriggerAge(Number.isNaN(value) ? null : value);
    };

    const commitEventAgeChange = () => {
        const nextLayer: AdditionalDistributionLayer = {
            ...layer,
            triggerAge: pendingTriggerAge
        };
        onChange(nextLayer);
    };

    const handleEventNameChange = (value: string) => {
        setPendingTriggerEventText(value);
    };

    const commitEventNameChange = () => {
        const nextLayer: AdditionalDistributionLayer = {
            ...layer,
            triggerEventText: pendingTriggerEventText
        };
        onChange(nextLayer);
    };

    const handleEventNameBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        commitEventNameChange();

        const nextTarget = event.relatedTarget;
        if (!(nextTarget instanceof HTMLButtonElement) || nextTarget.id !== "save_new_client_profile_button") {
            return;
        }

        let saveClickReceived = false;
        const markSaveClickReceived = () => {
            saveClickReceived = true;
        };

        nextTarget.addEventListener("click", markSaveClickReceived, {once: true});

        window.setTimeout(() => {
            nextTarget.removeEventListener("click", markSaveClickReceived);
            if (!saveClickReceived && document.body.contains(nextTarget)) {
                nextTarget.click();
            }
        }, 0);
    };

    const isAgeRequired = layer.triggerEventType === "AGE_ATTAINMENT";
    const isEventTextRequired = layer.triggerEventType === "OTHER";
    const missingAge = isAgeRequired && (pendingTriggerAge === null || pendingTriggerAge === undefined);
    const ageIsZero = isAgeRequired && pendingTriggerAge === 0;
    const missingAgeError = saveHasBeenClicked && missingAge ? "Age must be a number greater than 0" : undefined;
    const ageError = ageIsZero ? "Age must be a number greater than 0" : missingAgeError;
    const eventTextTrimmed = pendingTriggerEventText.trim();
    const missingEventText = isEventTextRequired && !eventTextTrimmed;
    const eventNameTooLong = isEventTextRequired && pendingTriggerEventText.length > EVENT_NAME_MAX_LENGTH;
    const missingEventNameError = saveHasBeenClicked && missingEventText ? "Event name is required." : undefined;
    const eventNameError = eventNameTooLong
        ? `Event Name cannot exceed ${EVENT_NAME_MAX_LENGTH} characters`
        : missingEventNameError;

    return (
        <section className="additional-distribution-layer-event-trigger-section">
            <div className="additional-distribution-layer-event-trigger-header">
                <span className="underlined-header__left-content">
                    <h4>Distribution Event Trigger</h4>
                </span>
                <Button
                    data-testid="delete-trash-icon"
                    className="delete-trash-icon"
                    ariaLabel="delete-trash-icon"
                    onClick={onReset}
                    icon="left"
                    iconName="delete"
                    kind="borderless"
                    style={{
                        color: "#3D4042",
                        cursor: "pointer",
                        minWidth: "40px"
                    }}
                />
            </div>
            <div className="additional-distribution-layer-event-trigger-subsection">
                <RadioGroupField
                    id="distribution-event-trigger"
                    label={Labels.DISTRIBUTION_EVENT_TRIGGER}
                    values={TRIGGER_EVENT_OPTIONS.map((option) => option.label)}
                    selectedValue={TRIGGER_EVENT_OPTIONS.find((option) => option.value === layer.triggerEventType)?.label ?? ""}
                    onChange={handleEventTriggerChange}
                    subtext={Labels.DISTRIBUTION_EVENT_SUBTEXT}
                    className="additional-distribution-layer-radio-group-field"
                />
                <div className="additional-distribution-layer-inputs">
                    {layer.triggerEventType === "DEATH" && (
                        <div className="additional-distribution-layer-death-section">
                            <DropdownField
                                value={getTriggerMemberValue(layer)}
                                label={Labels.DISTRIBUTION_CONSTANTS_DEATH}
                                required={true}
                                items={createFamilyMemberDropdownItems(
                                    parsedFamilyMembers.familyMember,
                                    getTriggerMemberSelection(layer),
                                    true
                                )}
                                onChange={handleSelectedMemberChange}
                                dataTestId="event-trigger-death-dropdown"
                            />
                        </div>
                    )}
                    {layer.triggerEventType === "AGE_ATTAINMENT" && (
                        <div className="additional-distribution-layer-age-attainment-section">
                            <div className="additional-distribution-layer-age-attainment-dropdown">
                                <DropdownField
                                    value={getTriggerMemberValue(layer)}
                                    label={Labels.DISTRIBUTION_CONSTANTS_AGE_ATTAINMENT}
                                    required={true}
                                    items={createFamilyMemberDropdownItems(
                                        parsedFamilyMembers.familyMember,
                                        getTriggerMemberSelection(layer),
                                        true
                                    )}
                                    onChange={handleSelectedMemberChange}
                                    dataTestId="event-trigger-age-attainment-dropdown"
                                />
                            </div>
                            <div className="additional-distribution-layer-age-attainment-input">
                                <AgeInput
                                    className="additional-distribution-layer-age-attainment-input"
                                    age={pendingTriggerAge ?? 0}
                                    onChange={handleEventAgeChange}
                                    onBlur={commitEventAgeChange}
                                    error={ageError}
                                    forceShowErrors={saveHasBeenClicked}
                                    label="What Age?"
                                />
                            </div>
                        </div>
                    )}
                    {layer.triggerEventType === "OTHER" && (
                        <div className="additional-distribution-layer-other-input">
                            <label className="h5 additional-distribution-layer-event-name-label" htmlFor="additional-distribution-event-name">
                                {Labels.DISTRIBUTION_CONSTANTS_OTHER}
                                <RedAsterisk />
                            </label>
                            <Input
                                className="additional-distribution-layer-other-input"
                                id="additional-distribution-event-name"
                                name="additional-distribution-event-name"
                                value={pendingTriggerEventText}
                                removeMarginTop
                                size="medium"
                                type="text"
                                disabled={false}
                                error={eventNameError}
                                forceShowErrors={saveHasBeenClicked || eventNameTooLong}
                                onChange={(e) => handleEventNameChange((e.target as HTMLInputElement).value)}
                                onBlur={handleEventNameBlur}
                            />
                            {!eventNameError && (
                                <span className="input__info margintop-sm additional-distribution-layer-event-name-helper">
                                    Maximum {EVENT_NAME_MAX_LENGTH} characters
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default AdditionalDistributionLayerEventTrigger;
